import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  CAPACITY_BANDS,
  CAPACITY_LABELS,
  CreateProspectInput,
  RECOMMENDATION_TYPE_LABELS,
  RECOMMENDATION_TYPES,
  type CapacityBand,
  type RecommendationType,
} from '@hv/shared';
import { useCreateProspect } from '../api/hooks';
import { describeError, useNotify } from '../components/Notify';
import { useCycleParam } from '../urlState';

interface Child {
  firstName: string;
  gradeApplying: string;
}

export function AddProspectDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const cycleId = useCycleParam();
  const { notify } = useNotify();
  const create = useCreateProspect();
  const [family, setFamily] = useState('');
  const [parents, setParents] = useState('');
  const [children, setChildren] = useState<Child[]>([{ firstName: '', gradeApplying: '' }]);
  const [withRec, setWithRec] = useState(true);
  const [recType, setRecType] = useState<RecommendationType>('trustee');
  const [recommender, setRecommender] = useState('');
  const [connection, setConnection] = useState('');
  const [valueAdd, setValueAdd] = useState('');
  const [narrative, setNarrative] = useState('');
  const [capacity, setCapacity] = useState<CapacityBand>('unknown');
  const [errors, setErrors] = useState<string[]>([]);

  const updateChild = (i: number, patch: Partial<Child>) => setChildren((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const candidate = {
      cycleId,
      familyLastName: family,
      parentNames: parents || null,
      applicants: children.map((c) => ({ firstName: c.firstName, gradeApplying: c.gradeApplying || null })),
      narrative: narrative || null,
      recommendation: withRec
        ? {
            type: recType,
            recommenderName: recommender,
            connection: connection || null,
            valueAdd: valueAdd || null,
            familyDetails: null,
            academicStanding: null,
            disciplinaryStanding: null,
            willEnroll: null,
            additionalInfo: null,
          }
        : null,
      capacityBand: capacity,
    };
    const parsed = CreateProspectInput.safeParse(candidate);
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => `${i.path.join('.') || 'form'}: ${i.message}`));
      return;
    }
    setErrors([]);
    create.mutate(parsed.data, {
      onSuccess: (res) => {
        notify('success', `Prospect ${parsed.data.familyLastName} created`);
        onCreated(res.id);
      },
      onError: (err) => {
        console.error('Creating prospect failed', err);
        setErrors([describeError(err)]);
      },
    });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="add-prospect-title">
      <Box component="form" onSubmit={submit} noValidate>
        <DialogTitle id="add-prospect-title">Add Prospect</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            For a family that has not applied yet. The profile merges with Veracross data once they apply.
          </Typography>
          {errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.map((e) => (
                <div key={e}>{e}</div>
              ))}
            </Alert>
          )}
          <Stack gap={2}>
            <TextField label="Family last name" required value={family} onChange={(e) => setFamily(e.target.value)} autoFocus />
            <TextField label="Parent names" value={parents} onChange={(e) => setParents(e.target.value)} />
            <Box>
              <Typography variant="h4" component="h3" mb={1}>
                Applicants
              </Typography>
              <Stack gap={1}>
                {children.map((c, i) => (
                  <Stack direction="row" gap={1} key={i} alignItems="center">
                    <TextField size="small" label={`Child ${i + 1} first name`} required value={c.firstName} onChange={(e) => updateChild(i, { firstName: e.target.value })} sx={{ flex: 1 }} />
                    <TextField size="small" label="Grade" value={c.gradeApplying} onChange={(e) => updateChild(i, { gradeApplying: e.target.value })} sx={{ width: 110 }} />
                    <IconButton aria-label={`Remove child ${i + 1}`} disabled={children.length === 1} onClick={() => setChildren((cs) => cs.filter((_, j) => j !== i))}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setChildren((cs) => [...cs, { firstName: '', gradeApplying: '' }])} disabled={children.length >= 10} sx={{ mt: 1 }}>
                Add another child
              </Button>
            </Box>
            <FormControlLabel control={<Checkbox checked={withRec} onChange={(e) => setWithRec(e.target.checked)} />} label="Include a recommendation" />
            {withRec && (
              <Stack gap={2}>
                <Stack direction="row" gap={1}>
                  <TextField select label="Recommendation type" value={recType} onChange={(e) => setRecType(e.target.value as RecommendationType)} sx={{ width: 220 }}>
                    {RECOMMENDATION_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {RECOMMENDATION_TYPE_LABELS[t]}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Recommender" required value={recommender} onChange={(e) => setRecommender(e.target.value)} sx={{ flex: 1 }} />
                </Stack>
                <TextField label="Connection" value={connection} onChange={(e) => setConnection(e.target.value)} multiline minRows={2} />
                <TextField label="How can this family add value?" value={valueAdd} onChange={(e) => setValueAdd(e.target.value)} multiline minRows={2} />
              </Stack>
            )}
            <TextField label="Impact narrative" placeholder="Boards, philanthropy, affiliations, capacity…" value={narrative} onChange={(e) => setNarrative(e.target.value)} multiline minRows={3} />
            <TextField select label="Estimated capacity" value={capacity} onChange={(e) => setCapacity(e.target.value as CapacityBand)}>
              {CAPACITY_BANDS.map((b) => (
                <MenuItem key={b} value={b}>
                  {CAPACITY_LABELS[b]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={create.isPending}>
            Create prospect
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
